import React, { useState, useEffect, useMemo } from "react";
import "../App.css";
import { groupTheme } from "../components/utils/groupTheme.jsx";
import axios from "axios";
import Card from "../components/Card.jsx";

function CardStats({ user }) {
  const [loading, setLoading] = useState(true);
  const [deckGroups, setDeckGroups] = useState([]);
  const [decks, setDecks] = useState([]);
  const [cards, setCards] = useState([]);
  const [sortOption, setSortOption] = useState("mostDecks");
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [selectedRank, setSelectedRank] = useState(null);

  useEffect(() => {
    async function fetchAllData() {
      if (!user?.id) return;

      setLoading(true);

      try {
        const [cardsRes, decksRes, deckGroupsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}api/data/snapRankings/${user.id}`),
          axios.get(`${process.env.REACT_APP_API_URL}api/data/snapDecks/user/${user.id}`),
          axios.get(`${process.env.REACT_APP_API_URL}api/data/snapDividors/${user.id}`),
        ]);

        setCards(Array.isArray(cardsRes.data) ? cardsRes.data : []);
        setDecks(Array.isArray(decksRes.data) ? decksRes.data : []);
        setDeckGroups(Array.isArray(deckGroupsRes.data) ? deckGroupsRes.data : []);
      } catch (err) {
        console.error("Failed loading card stats data:", err);
        setCards([]);
        setDecks([]);
        setDeckGroups([]);
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, [user]);

  const cardUsageMap = useMemo(() => {
    const usage = {};

    decks.forEach((deck) => {
      const uniqueCardsInDeck = new Set();

      for (let i = 1; i <= 12; i++) {
        const cardId = deck?.[`card${i}`];
        if (cardId != null) {
          uniqueCardsInDeck.add(cardId);
        }
      }

      uniqueCardsInDeck.forEach((cardId) => {
        usage[cardId] = (usage[cardId] || 0) + 1;
      });
    });

    return usage;
  }, [decks]);

  const cardInfiniteMap = useMemo(() => {
    const usage = {};
    const infDecks = decks.filter((deck) => deck.deckrank === "Infinite Runners");

    infDecks.forEach((deck) => {
      const uniqueCardsInDeck = new Set();

      for (let i = 1; i <= 12; i++) {
        const cardId = deck?.[`card${i}`];
        if (cardId != null) {
          uniqueCardsInDeck.add(cardId);
        }
      }

      uniqueCardsInDeck.forEach((cardId) => {
        usage[cardId] = (usage[cardId] || 0) + 1;
      });
    });

    return usage;
  }, [decks]);

  const sortedCards = useMemo(() => {
    const validCards = (cards || []).filter((card) => card && card.cardid != null);
    const cardsCopy = [...validCards];

    if (sortOption === "mostDecks") {
      cardsCopy.sort((a, b) => {
        const bCount = cardUsageMap[b.cardid] || 0;
        const aCount = cardUsageMap[a.cardid] || 0;

        if (bCount !== aCount) return bCount - aCount;

        return (a.cardname || "").localeCompare(b.cardname || "");
      });
    }

    if (sortOption === "mostInfiniteDecks") {
      cardsCopy.sort((a, b) => {
        const bCount = cardInfiniteMap[b.cardid] || 0;
        const aCount = cardInfiniteMap[a.cardid] || 0;

        if (bCount !== aCount) return bCount - aCount;

        return (a.cardname || "").localeCompare(b.cardname || "");
      });
    }

    if (sortOption === "highestReliance") {
      cardsCopy.sort((a, b) => {
        return (b.reliancescore ?? 0) - (a.reliancescore ?? 0);
      });
    }

    if (sortOption === "highestVariance") {
      cardsCopy.sort((a, b) => {
        return (b.variantsscore ?? 0) - (a.variantsscore ?? 0);
      });
    }

    if (sortOption === "highestUsability") {
      cardsCopy.sort((a, b) => {
        return (b.usabilityscore ?? 0) - (a.usabilityscore ?? 0);
      });
    }

    return cardsCopy;
  }, [cards, cardUsageMap, cardInfiniteMap, sortOption]);

  return (
    <div className="card-stats-page">
      <h1 className="main-title">Card Stats</h1>

      <div className="styled-info-modal" style={{ marginBottom: 40 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, max-content)", gap: "16px 18px" }}>
            <button className="button" onClick={() => setSortOption("highestReliance")} type="button">Highest Reliance</button>
            <button className="button" onClick={() => setSortOption("highestVariance")} type="button">Highest Variance</button>
            <button className="button" onClick={() => setSortOption("highestUsability")} type="button">Highest Usability</button>
            <button className="button" onClick={() => setSortOption("mostDecks")} type="button">Most Decks</button>
            <button className="button" onClick={() => setSortOption("mostInfiniteDecks")} type="button">Most Infinite Decks</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, max-content)", gap: "16px 18px" }}>
            <button className="button">Threshold: Top _%</button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="list-group">
          <span className="header-title">Loading card stats...</span>
        </div>
      ) : sortedCards.length === 0 ? (
        <div className="list-group">
          <span className="header-title">No cards found.</span>
        </div>
      ) : (
        <div className="list-group">
          <div className="top5-wrap">
            {sortedCards.slice(0, 5).map((card, idx) => {
              const theme = groupTheme(card.cardname);
              const mt = idx === 0 ? "0" : idx === 1 ? "12px" : "24px";
              const usageCount = cardUsageMap[card.cardid] || 0;
              const infiniteCount = cardInfiniteMap[card.cardid] || 0;
              const deckPercent =
                decks.length > 0
                  ? ((usageCount / decks.length) * 100).toFixed(1)
                  : "0.0";

              const imageSrc =
                card.customhtml && card.customhtml.trim() !== ""
                  ? card.customhtml 
                  : card.cardHTML || card.cardhtml;

              return (
                <div
                  key={card.id}
                  className="top5-card"
                  style={{
                    ...theme.wrap,
                    borderRadius: 14,
                    padding: 18,
                    marginTop: mt,
                  }}
                >
                  {sortOption === "mostDecks" && <div className="styled-info-modal">{deckPercent}% Usage</div>}
                  {sortOption === "mostInfiniteDecks" && <div className="styled-info-modal">{infiniteCount} Infinite Decks</div>}
                  {sortOption === "highestReliance" && <div className="styled-info-modal">{card.reliancescore ?? 0}% Reliance</div>}
                  {sortOption === "highestVariance" && <div className="styled-info-modal">{card.variantsscore ?? 0}% Variants</div>}
                  {sortOption === "highestUsability" && <div className="styled-info-modal">{card.usabilityscore ?? 0}% Usability</div>}
                  <img
                    src={imageSrc || ""}
                    alt={card.cardname || "Card"}
                    className="deck-card-img"
                  />
                </div>
              );
            })}
          </div>

          <div
            className="card-grid"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
          >
            {sortedCards.slice(5).map((card, index) => {
              const usageCount = cardUsageMap[card.cardid] || 0;
              const infiniteCount = cardInfiniteMap[card.cardid] || 0;
              const deckPercent =
                decks.length > 0
                  ? ((usageCount / decks.length) * 100).toFixed(1)
                  : "0.0";

              const imageSrc =
                card.customhtml && card.customhtml.trim() !== ""
                  ? card.customhtml 
                  : card.cardHTML || card.cardhtml;

              return (
                <div key={card.cardid} className="card-snap">
                  {sortOption === "mostDecks" && <div className="styled-info-modal">{deckPercent}% Usage</div>}
                  {sortOption === "mostInfiniteDecks" && <div className="styled-info-modal">{infiniteCount} Infinite Decks</div>}
                  {sortOption === "highestReliance" && <div className="styled-info-modal">{card.reliancescore ?? 0}% Reliance</div>}
                  {sortOption === "highestVariance" && <div className="styled-info-modal">{card.variantsscore ?? 0}% Variants</div>}
                  {sortOption === "highestUsability" && <div className="styled-info-modal">{card.usabilityscore ?? 0}% Usability</div>}
                  <img
                    src={imageSrc}
                    alt={card.cardname || "Card"}
                    className="deck-card-img"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default CardStats;